import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import { useLocation } from '@reach/router';
import Icon from 'components/UI/Icon';
import Link from 'components/UI/Link';
import SubMenu from '../SubMenu';
import { isRequestedFrom, useLang } from 'utils/localisation';
import {
  DOMAIN_URL,
  DOMAIN_URL_AE,
  DOMAIN_URL_DE,
  DOMAIN_URL_AZ,
  DOMAIN_URL_FR,
  DOMAIN_URL_NL,
} from 'define';
import { arabicRegion, azerRegion } from 'define/locale';
import { getHeaderDeviceType } from 'utils';
import { excludedPages } from './constants';
import { pushHeaderEvent } from '../../utils';
import { useWindowResize } from 'utils/hooks';

import * as styles from './LangSwitcher.module.scss';

function LangSwitcher({ isIndexPage, className }) {
  const { width } = useWindowResize();
  const deviceType = getHeaderDeviceType(width);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const location = useLocation();

  const lang = useLang();
  const langForSwitcher = lang === 'ae' ? 'ar' : lang;

  const submenuLangs = useMemo(() => {
    if (langForSwitcher === 'de') {
      return [
        { title: 'DE', url: `${DOMAIN_URL_DE}` },
        { title: 'EN', url: `${DOMAIN_URL}` },
      ];
    }

    if (langForSwitcher === 'az') {
      return [
        { title: 'AZ', url: `${DOMAIN_URL_AZ}` },
        { title: 'EN', url: `${DOMAIN_URL}` },
      ];
    }

    if (langForSwitcher === 'fr') {
      return [
        { title: 'FR', url: `${DOMAIN_URL_FR}` },
        { title: 'EN', url: `${DOMAIN_URL}` },
      ];
    }

    if (langForSwitcher === 'nl') {
      return [
        { title: 'NL', url: `${DOMAIN_URL_NL}` },
        { title: 'EN', url: `${DOMAIN_URL}` },
      ];
    }

    if (langForSwitcher === 'en' && isRequestedFrom(azerRegion)) {
      return [
        { title: 'EN', url: `${DOMAIN_URL}` },
        { title: 'AZ', url: `${DOMAIN_URL_AZ}` },
      ];
    }

    if (langForSwitcher === 'en' && isRequestedFrom('fr')) {
      return [
        { title: 'EN', url: `${DOMAIN_URL}` },
        { title: 'FR', url: `${DOMAIN_URL_FR}` },
      ];
    }

    if (langForSwitcher === 'en' && isRequestedFrom('NL')) {
      return [
        { title: 'EN', url: `${DOMAIN_URL}` },
        { title: 'NL', url: `${DOMAIN_URL_NL}` },
      ];
    }

    return [
      { title: 'EN', url: `${DOMAIN_URL}` },
      { title: 'DE', url: `${DOMAIN_URL_DE}` },
    ];
  }, [langForSwitcher, isIndexPage]);

  const singleLangLink = useMemo(() => {
    if (langForSwitcher === 'ar') {
      return { title: 'EN', url: `${DOMAIN_URL}` };
    }
    if (isIndexPage && langForSwitcher === 'en' && isRequestedFrom(arabicRegion)) {
      return { title: 'العربية', url: `${DOMAIN_URL_AE}` };
    }
    return null;
  }, [langForSwitcher, isIndexPage]);

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      setShowSubmenu(prev => !prev);
    } else if (e.key === 'Escape') {
      setShowSubmenu(false);
    }
  };

  const handleMouseLeave = () => {
    setShowSubmenu(false);
  };

  const handleClick = () => {
    setShowSubmenu(!showSubmenu);
  };

  if (
    excludedPages.includes(location.pathname) ||
    lang === 'uk' ||
    lang === 'pl' ||
    lang === 'kz'
  ) {
    return null;
  }

  return (
    <div className={cn(styles.langSwitcher, className)}>
      <div className={styles.list}>
        {singleLangLink ? (
          <div className={cn(styles.item, lang === 'en' && styles.withBorder)}>
            <Link
              key={langForSwitcher}
              href={singleLangLink.url}
              className={styles.linkWithoutArrow}
              tabIndex={0}
              onClick={() =>
                pushHeaderEvent('click_on_lang_switch', singleLangLink.title, deviceType)
              }
            >
              {singleLangLink.title}
            </Link>
          </div>
        ) : (
          <div
            key={langForSwitcher}
            className={cn(
              styles.item,
              styles.submenu,
              showSubmenu && styles.showSubmenu,
              lang === 'en' && styles.withBorder,
            )}
            role="button"
            aria-expanded={showSubmenu}
            aria-haspopup="menu"
            aria-label={`Language switcher. Current language: ${langForSwitcher.toUpperCase()}`}
            onClick={handleClick}
            onMouseLeave={handleMouseLeave}
          >
            <div className={cn(styles.link, styles.arrow)}>
              {langForSwitcher.toUpperCase()}
              <Icon
                tabIndex={0}
                onKeyDown={handleKeyDown}
                name="small-arrow"
                className={cn(styles.arrowIcon, styles.smallArrowIcon, styles.arrowRotated)}
              />
            </div>
            <SubMenu
              items={submenuLangs}
              currentLink={location.href}
              theme="langSwitcher"
              className={styles.submenu}
              withTransition={true}
              deviceType={deviceType}
              active={showSubmenu}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default LangSwitcher;
